import { ModalFooter } from '#pplx/frontend/aether/components/Modal/internal/ModalFooter.js';
import { ModalHeader, ModalTitle, } from '#pplx/frontend/aether/components/Modal/internal/ModalHeader.js';
import { cva } from 'class-variance-authority';
import classNames from 'classnames';
import { Children, isValidElement } from 'react';
// ---------------------------------------------------------------------------
// Styling helpers — shared
// ---------------------------------------------------------------------------
export const BACKDROP_DEFAULT = 'dim';
export function getOverlayClassName({ variant = BACKDROP_DEFAULT, } = {}) {
    return classNames(
    // Cover the entire viewport
    'fixed inset-0', {
        // Semi-transparent backdrop
        'bg-black/20': variant === 'side-sheet',
        'bg-black/50': variant === 'dim',
        'bg-transparent': variant === 'transparent',
    }, 'pointer-events-auto');
}
// ---------------------------------------------------------------------------
// Styling helpers — mobile bottom sheet (slide animation)
// ---------------------------------------------------------------------------
export function getBottomSheetContentClassName({ background = 'base', } = {}) {
    return classNames(background === 'transparent' ? 'bg-transparent' : 'bg-base', 
    // Pin to bottom of viewport
    'fixed inset-x-0 bottom-0', 
    // Layout and sizing
    'flex flex-col max-h-[90dvh]', 'shadow-lg rounded-t-xl');
}
// ---------------------------------------------------------------------------
// Styling helpers — desktop side sheet (slide animation)
// ---------------------------------------------------------------------------
export function getSideSheetContentClassName({ background = 'base', } = {}) {
    return classNames(background === 'transparent' ? 'bg-transparent' : 'bg-base', 
    // Inset the sheet from the viewport edge while keeping it anchored right on desktop.
    'fixed inset-4 md:left-auto', 
    // Layout and sizing
    'pointer-events-auto flex min-w-0 flex-col justify-start md:w-[40vw] md:min-w-[400px] md:max-w-[560px]', 'rounded-3xl shadow-md');
}
// ---------------------------------------------------------------------------
// Styling helpers — chromeless body
// ---------------------------------------------------------------------------
/**
 * Body class name for chromeless mobile modals. Adds a small gap between the
 * body and the (consumer-rendered) footer when one is present. Standard
 * (non-chromeless) modals don't need this — their padding is owned by the
 * panel, which already separates body and footer.
 */
export function getChromelessBottomSheetBodyClassName({ hasFooter, }) {
    return classNames({ 'pb-4': hasFooter });
}
// ---------------------------------------------------------------------------
// Styling helpers — panel-level padding
// ---------------------------------------------------------------------------
/**
 * Padding and inter-section gap applied once at the modal panel level.
 * Subcomponents (`Modal.Header` / `Modal.Body` / `Modal.Footer`) intentionally
 * don't apply their own padding or margins; the panel's flex column owns both
 * outer padding and the spacing between sections so nothing compounds.
 *
 * Skipped for chromeless modals — those intentionally have no chrome and let
 * consumers fully control layout.
 */
export function getPanelPaddingClassName({ chromeless, isMobile, }) {
    if (chromeless) {
        return '';
    }
    if (isMobile) {
        return 'gap-4 px-4 pt-4 pb-[max(var(--safe-area-inset-bottom),var(--spacing-4))]';
    }
    return 'gap-4 p-4';
}
export const desktopContentVariants = cva([
    // Background
    // Layout
    'relative pointer-events-auto flex flex-col',
], {
    variants: {
        background: {
            base: 'bg-base',
            transparent: 'bg-transparent',
        },
        size: {
            small: 'rounded-xl shadow-md max-h-[85vh] max-w-[400px] w-[calc(100vw-2*var(--spacing-8))]',
            medium: 'rounded-xl shadow-md max-h-[85vh] max-w-[560px] w-[calc(100vw-2*var(--spacing-8))]',
            large: 'rounded-3xl shadow-md max-h-[85vh] max-w-[720px] w-[calc(100vw-2*var(--spacing-8))]',
            xlarge: 'rounded-xl shadow-md h-[85vh] w-[85vw]',
            fullscreen: 'h-dvh w-[100vw] outline-hidden',
        },
    },
    defaultVariants: {
        background: 'base',
        size: 'medium',
    },
});
export function getContentAriaProps(chromeless, ariaProps) {
    if (chromeless) {
        return {
            'aria-describedby': ariaProps['aria-describedby'],
            'aria-description': ariaProps['aria-description'],
            'aria-label': ariaProps['aria-label'],
            'aria-labelledby': ariaProps['aria-labelledby'],
        };
    }
    return { 'aria-describedby': undefined };
}
export function getModalChildrenState(children) {
    const childArray = Children.toArray(children);
    const hasChildHeader = childArray.some((child) => isValidElement(child) && child.type === ModalHeader);
    const hasChildTitle = childArray.some((child) => isValidElement(child) && child.type === ModalTitle);
    const hasFooter = childArray.some((child) => isValidElement(child) && child.type === ModalFooter);
    return {
        childArray,
        hasChildHeader,
        hasFooter,
        hasTitle: hasChildHeader || hasChildTitle,
    };
}
export function getDefaultContentAriaProps({ childArray, hasChildHeader, }) {
    if (!hasChildHeader) {
        return { 'aria-describedby': undefined };
    }
    const headerChild = childArray.find((child) => isValidElement(child) && child.type === ModalHeader);
    const childDescription = isValidElement(headerChild) && headerChild.props
        ? headerChild.props.description
        : undefined;
    return childDescription ? undefined : { 'aria-describedby': undefined };
}
export function getSideSheetContentAriaProps(defaultAriaProps, ariaProps) {
    const contentAriaProps = { ...(defaultAriaProps ?? {}) };
    const ariaLabel = ariaProps['aria-label'];
    const ariaLabelledBy = ariaProps['aria-labelledby'];
    const ariaDescribedBy = ariaProps['aria-describedby'];
    const ariaDescription = ariaProps['aria-description'];
    if (ariaLabel !== undefined) {
        contentAriaProps['aria-label'] = ariaLabel;
        contentAriaProps['aria-labelledby'] = ariaLabelledBy;
    }
    if (ariaLabelledBy !== undefined) {
        contentAriaProps['aria-labelledby'] = ariaLabelledBy;
    }
    if (ariaDescribedBy !== undefined) {
        contentAriaProps['aria-describedby'] = ariaDescribedBy;
    }
    else if (ariaDescription !== undefined) {
        contentAriaProps['aria-describedby'] = undefined;
    }
    if (ariaDescription !== undefined) {
        contentAriaProps['aria-description'] = ariaDescription;
    }
    return contentAriaProps;
}
//# sourceMappingURL=ModalInternals.js.map